import {
  Image as ExpoImage,
  ImageProps as ExpoImageProps,
  ImageStyle as ExpoImageStyle,
} from 'expo-image';
import React from 'react';
import { Platform, StyleSheet, View, ViewProps } from 'react-native';

import { logError } from '@helpers/Loggers';
import { getCircleStyle } from '@helpers/Styles';

export type ImageProps = ExpoImageProps & {
  /** Renders the image as a circle based on its resolved numeric size. */
  circle?: boolean;
  disablePlaceholder?: boolean;
};

export default function Image({
  source,
  style,
  circle,
  disablePlaceholder,
  onError,
  ...props
}: ImageProps) {
  const imageStyle = [
    ...(Array.isArray(style) ? style : [style]),
    circle ? getCircleStyle(style) : undefined,
  ];

  // see here for reason behind with platform-specific line
  // - https://github.com/expo/expo/issues/23625
  const _style = Platform.select({
    default: imageStyle,
    web: [styles.webStyle, ...imageStyle],
  });

  return (
    <ExpoImage
      source={source}
      onError={(event) => {
        logError(new Error(event.error));
        onError?.(event);
      }}
      placeholder={!disablePlaceholder ? 'LHLENU~qM{xu00ayofj[xuWBofay' : undefined}
      transition={!disablePlaceholder ? 500 : undefined}
      {...props}
      style={_style}
    />
  );
}

export function clearImageCache() {
  ExpoImage.clearDiskCache();
  ExpoImage.clearMemoryCache();
}

type ImageBackgroundProps = Omit<ImageProps, 'style'> & {
  style?: ViewProps['style'];
  children?: ViewProps['children'];
  imageStyle?: ExpoImageStyle;
};

export function ImageBackground({ children, style, imageStyle, ...props }: ImageBackgroundProps) {
  return (
    <View style={style}>
      <Image style={[StyleSheet.absoluteFill, imageStyle]} {...props} />
      {children}
    </View>
  );
}

type LogoProps = Omit<ImageProps, 'source'> & {
  size?: number;
};

const LOGO_SIZE = 75;

export function Logo({ size, style, ...props }: LogoProps) {
  return (
    <Image
      source={require('../assets/images/logo.png')}
      disablePlaceholder
      {...props}
      style={[{ height: size || LOGO_SIZE, width: size || LOGO_SIZE }, style]}
    />
  );
}

const styles = StyleSheet.create({
  webStyle: {
    height: '100%',
  },
});
